import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Tab } from '@headlessui/react';
import { formatDate } from '../../utils/dateUtils';
import { impersonateUser } from '../../store/slices/authSlice';
import { 
  getAllUsers, 
  getAllCompanies, 
  toggleUserStatus,
  User as ServiceUser,
  Company as ServiceCompany
} from '../../services/adminService';
import UserPopup from '../../components/admin/UserPopup';
import CompanyPopup from '../../components/admin/CompanyPopup';
import CreateUserPopup from '../../components/admin/CreateUserPopup';
import CreateClientPopup from '../../components/admin/CreateClientPopup';
import { 
  showSuccessToast, 
  showErrorToast, 
  showImpersonationToast, 
  dismissImpersonationToasts 
} from '../../utils/toastUtils';

// Reuse the service interfaces for consistency
type User = ServiceUser;
type Company = ServiceCompany;

// Format date/time from ISO string
const formatDateTime = (isoString?: string) => {
  if (!isoString) return 'Jamais';
  return formatDate(isoString);
};

const AdminDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // State for edit popups
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isUserPopupOpen, setIsUserPopupOpen] = useState<boolean>(false);
  const [isCompanyPopupOpen, setIsCompanyPopupOpen] = useState<boolean>(false);
  
  // State for create popups
  const [isCreateUserPopupOpen, setIsCreateUserPopupOpen] = useState<boolean>(false);
  const [isCreateClientPopupOpen, setIsCreateClientPopupOpen] = useState<boolean>(false);

  // Clean up any lingering impersonation toasts
  useEffect(() => {
    // Clean up on mount
    dismissImpersonationToasts();
    
    // Clean up on unmount
    return () => {
      dismissImpersonationToasts();
    };
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersData, companiesData] = await Promise.all([
          getAllUsers(),
          getAllCompanies()
        ]);
        setUsers(usersData);
        setCompanies(companiesData);
      } catch (error) {
        console.error('Error fetching data:', error);
        showErrorToast('Erreur lors du chargement des données', 'load-data-error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const searchString = searchTerm.toLowerCase();
    return (
      user.firstName.toLowerCase().includes(searchString) ||
      user.lastName.toLowerCase().includes(searchString) ||
      user.email.toLowerCase().includes(searchString) ||
      user.role.toLowerCase().includes(searchString) ||
      (user.company?.name && user.company.name.toLowerCase().includes(searchString))
    );
  });

  // Filter companies based on search term
  const filteredCompanies = companies.filter(company => {
    const searchString = searchTerm.toLowerCase();
    return (
      company.name.toLowerCase().includes(searchString) ||
      company.siret.toLowerCase().includes(searchString) ||
      (company.city && company.city.toLowerCase().includes(searchString)) ||
      (company.departement && company.departement.toLowerCase().includes(searchString))
    );
  });

  // Handle impersonation
  const handleImpersonate = async (userId: number) => {
    try {
      // Find the user to get their name
      const targetUser = users.find(user => user.id === userId);
      const userName = targetUser ? `${targetUser.firstName} ${targetUser.lastName}` : '';
      
      await dispatch(impersonateUser(userId) as any);
      
      // Use the special impersonation toast handler
      showImpersonationToast(userName);
      
      // Navigation will happen automatically through auth state change
    } catch (error) {
      console.error('Impersonation error:', error);
      showErrorToast('Erreur lors de l\'impersonation', 'impersonate-error');
    }
  };

  // Handle toggle user status
  const handleToggleStatus = async (userId: number, currentStatus: boolean) => {
    try {
      await toggleUserStatus(userId);
      // Update users state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, isActive: !currentStatus } : user
      ));
      showSuccessToast(
        `L'utilisateur est maintenant ${!currentStatus ? 'actif' : 'inactif'}`,
        `toggle-status-${userId}`
      );
    } catch (error) {
      console.error('Toggle status error:', error);
      showErrorToast('Erreur lors du changement de statut', `toggle-status-error-${userId}`);
    }
  };

  // Handle edit user
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsUserPopupOpen(true);
  };

  // Handle edit company
  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setIsCompanyPopupOpen(true);
  };

  // Handle user update
  const handleUserUpdated = (updatedUser: User) => {
    setUsers(users.map(user => 
      user.id === updatedUser.id ? updatedUser : user
    ));
    setIsUserPopupOpen(false);
    showSuccessToast('Utilisateur mis à jour avec succès', `user-updated-${updatedUser.id}`);
  };

  // Handle company update
  const handleCompanyUpdated = (updatedCompany: Company) => {
    setCompanies(companies.map(company => 
      company.id === updatedCompany.id ? updatedCompany : company
    ));
    setIsCompanyPopupOpen(false);
    showSuccessToast('Entreprise mise à jour avec succès', `company-updated-${updatedCompany.id}`);
  };
  
  // Handle new user creation
  const handleUserCreated = (newUser: User) => {
    setUsers([newUser, ...users]);
    setIsCreateUserPopupOpen(false);
    showSuccessToast('Utilisateur créé avec succès', `user-created-${newUser.id}`);
  };
  
  // Handle new client creation
  const handleClientCreated = (data: { user: User; company: Company }) => {
    setUsers([data.user, ...users]);
    setCompanies([data.company, ...companies]);
    setIsCreateClientPopupOpen(false);
    showSuccessToast('Client créé avec succès', `client-created-${data.user.id}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Administration</h1>
      
      <div className="mb-6">
        <input
          type="text"
          placeholder="Rechercher..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <Tab.Group>
        <Tab.List className="flex space-x-1 rounded-xl bg-indigo-50 p-1 mb-6">
          <Tab 
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 
              ${selected 
                ? 'bg-white shadow text-indigo-700' 
                : 'text-gray-600 hover:bg-white/[0.12] hover:text-gray-700'}`
            }
          >
            Utilisateurs ({users.length})
          </Tab>
          <Tab 
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 
              ${selected 
                ? 'bg-white shadow text-indigo-700' 
                : 'text-gray-600 hover:bg-white/[0.12] hover:text-gray-700'}`
            }
          >
            Entreprises ({companies.length})
          </Tab>
        </Tab.List>
        <Tab.Panels className="mt-2">
          <Tab.Panel>
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setIsCreateUserPopupOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded shadow-sm"
              >
                Créer un utilisateur
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 shadow-md rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dernière connexion</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.firstName} {user.lastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.role === 'ROLE_ADMIN' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user.role === 'ROLE_ADMIN' ? 'Admin' : 'Utilisateur'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.company?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.isActive ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatDateTime(user.lastLoginAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button 
                          onClick={() => handleImpersonate(user.id)}
                          className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 px-2 py-1 rounded"
                        >
                          Impersonate
                        </button>
                        <button 
                          onClick={() => handleToggleStatus(user.id, user.isActive)}
                          className={`hover:text-white px-2 py-1 rounded ${
                            user.isActive 
                              ? 'text-red-600 hover:bg-red-600 bg-red-50' 
                              : 'text-green-600 hover:bg-green-600 bg-green-50'
                          }`}
                        >
                          {user.isActive ? 'Désactiver' : 'Activer'}
                        </button>
                        <button 
                          onClick={() => handleEditUser(user)}
                          className="text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 px-2 py-1 rounded"
                        >
                          Modifier
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Tab.Panel>
          <Tab.Panel>
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setIsCreateClientPopupOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded shadow-sm"
              >
                Créer un nouveau client
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 shadow-md rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SIRET</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Localisation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Codes NAF</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateurs</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérents</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Création</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredCompanies.map((company) => (
                    <tr key={company.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {company.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {company.siret}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {company.city && company.departement 
                          ? `${company.city} (${company.departement})` 
                          : company.city || company.departement || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {company.nafCodes && company.nafCodes.length > 0 
                            ? company.nafCodes.map((code, index) => (
                                <span 
                                  key={index} 
                                  className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800"
                                >
                                  {code}
                                </span>
                              ))
                            : '-'
                          }
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {company.userCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {company.adherentCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatDateTime(company.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => handleEditCompany(company)}
                          className="text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 px-2 py-1 rounded"
                        >
                          Modifier
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* Edit Popups */}
      {selectedUser && (
        <UserPopup 
          user={selectedUser}
          isOpen={isUserPopupOpen}
          onClose={() => setIsUserPopupOpen(false)}
          onUserUpdated={handleUserUpdated}
        />
      )}

      {selectedCompany && (
        <CompanyPopup 
          company={selectedCompany}
          isOpen={isCompanyPopupOpen}
          onClose={() => setIsCompanyPopupOpen(false)}
          onCompanyUpdated={handleCompanyUpdated}
        />
      )}
      
      {/* Create Popups */}
      <CreateUserPopup
        isOpen={isCreateUserPopupOpen}
        onClose={() => setIsCreateUserPopupOpen(false)}
        onUserCreated={handleUserCreated}
      />
      
      <CreateClientPopup
        isOpen={isCreateClientPopupOpen}
        onClose={() => setIsCreateClientPopupOpen(false)}
        onClientCreated={handleClientCreated}
      />
    </div>
  );
};

export default AdminDashboard; 